import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {AuthApi, AuthenticationType} from "components/src/api/admin/generated";
import {ChangePassword} from "components/src/auth/ChangePassword";
import {TfaSetupWizard} from "components/src/auth/TfaSetupWizard";
import {useRenderOnce} from "components/src/hook/UseRenderOnce";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {startTransition, useState} from "react";
import NavDropdown from "react-bootstrap/NavDropdown";
import {createPortal} from "react-dom";
import {useNavigate} from "react-router-dom";

export const AppMenu = () =>
{
    const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
    const [showSetup2FADialog, setShowSetup2FADialog] = useState(false);
    const [authenticationType, setAuthenticationType] = useState<AuthenticationType>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    const navigate = useNavigate();

    const authApi = new AuthApi(ApiConfig.config());

    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    useRenderOnce(() =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        authApi.authControllerGetCallerAuthenticationType()
            .then(response => {
                setAuthenticationType(response.data)
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error get authentication type", err);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    })

    function isEnableChangePassword() {
        return authenticationType === AuthenticationType.UsernamePassword;
    }

    function isEnableTFASetup() {
        return authenticationType === AuthenticationType.UsernamePassword || authenticationType == AuthenticationType.Ldap;
    }

    return (
        <>
            {/* ChangePassword Modal should be placed outside this menu */}
            {showChangePasswordDialog && createPortal(
                <ChangePassword changePasswordFunc={(param) => authApi.authControllerChangePassword(param)} onClose={() => setShowChangePasswordDialog(false)}/>,
                document.body
            )}

            {/* TfaSetupWizard Modal should be placed outside this menu */}
            {showSetup2FADialog && createPortal(
                <TfaSetupWizard
                    is2FAEnabledFunc={() => authApi.authControllerGetCaller2FAEnabled()}
                    generateSecretFunc={() => authApi.authControllerGenerateTFASecret()}
                    createSecretQRCodeFunc={(secret : string) => authApi.authControllerCreateCaller2FASecretQRCode(secret)}
                    set2FAEnabledFunc={(enabled : boolean) => authApi.authControllerSetCaller2FAEnabled(enabled)}
                    validateOTPFunc={(otpCode : string, secret : string) => authApi.authControllerValidateTOTP({otp : otpCode, tfaSecret : secret})}
                    setSecretFunc={(secret : string) => authApi.authControllerSetCaller2FASecret(secret)}
                    onClose={() => setShowSetup2FADialog(false)}/>,
                document.body
            )}

            <NavDropdown.Item onClick={() => startTransition(() => navigate("/shell"))}>
                {t('shell.menu')}
            </NavDropdown.Item>
            <NavDropdown.Divider/>

            {/* startTransition is required to prevent "A component suspended while responding to synchronous input..." */}
            <NavDropdown.Item onClick={() => startTransition(() => setShowChangePasswordDialog(true))}
                              disabled={!isEnableChangePassword()}>
                {t('common.change-password')}
            </NavDropdown.Item>
            <NavDropdown.Divider/>
            <NavDropdown.Item onClick={() => startTransition(() => setShowSetup2FADialog(true))}
                              disabled={!isEnableTFASetup()}>
                {t('common.setup-2fa')}
            </NavDropdown.Item>
            <NavDropdown.Divider/>
        </>
    )
}
